import React, { useState, useEffect, useRef } from 'react';
import { 
  FaFolder, 
  FaFolderOpen, 
  FaFileAlt, 
  FaFilePdf, 
  FaFileImage, 
  FaFileCode, 
  FaFileArchive,
  FaFileCsv,
  FaFileExcel,
  FaFileWord,
  FaFilePowerpoint,
  FaTimes, 
  FaChevronRight, 
  FaChevronDown,
  FaDownload,
  FaSearch
} from 'react-icons/fa';
import { getCookie } from './auth/cookies';
import { toast } from 'react-toastify';

function FileExplorerModal({ files, onClose }) {
    const [fileStructure, setFileStructure] = useState({});
    const [openFolders, setOpenFolders] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredStructure, setFilteredStructure] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const modalRef = useRef(null);

    // Organize files into a hierarchical structure whenever the files prop changes
    useEffect(() => {
        organizeFiles(files);
    }, [files]);

    // Filter files based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredStructure(fileStructure);
            return;
        }

        const filterStructure = (structure, path = '') => {
            const result = {};
            
            Object.keys(structure).forEach(key => {
                const isFile = typeof structure[key] === 'string';
                const currentPath = path ? `${path}/${key}` : key;
                
                if (isFile) {
                    // If it's a file and matches the search term, include it
                    if (key.toLowerCase().includes(searchTerm.toLowerCase())) {
                        result[key] = structure[key];
                    }
                } else {
                    // If it's a folder, recursively filter its contents
                    const filteredContents = filterStructure(structure[key], currentPath);
                    
                    // If the folder name matches or it has matching contents, include it
                    if (key.toLowerCase().includes(searchTerm.toLowerCase()) || Object.keys(filteredContents).length > 0) {
                        result[key] = filteredContents;
                        
                        // Automatically open folders in search results
                        if (Object.keys(filteredContents).length > 0) {
                            setOpenFolders(prev => ({
                                ...prev,
                                [currentPath]: true
                            }));
                        }
                    }
                }
            });
            
            return result;
        };
        
        setFilteredStructure(filterStructure(fileStructure));
    }, [searchTerm, fileStructure]);

    // Handle click outside to close modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [onClose]);

    // Organize files into a hierarchical structure
    const organizeFiles = (fileArray) => {
        const structure = {};
        fileArray.forEach((filePath) => {
            const parts = filePath.split('/').filter(Boolean);
            let current = structure;
            parts.forEach((part, index) => {
                if (!current[part]) {
                    current[part] = index === parts.length - 1 ? filePath : {};
                }
                
                if (typeof current[part] !== 'string') {
                    current = current[part];
                }
            });
        });
        setFileStructure(structure);
        setFilteredStructure(structure);
    };

    // Toggle folder open/close state
    const toggleFolder = (folderPath, event) => {
        event.stopPropagation();
        setOpenFolders((prevOpenFolders) => ({
            ...prevOpenFolders,
            [folderPath]: !prevOpenFolders[folderPath],
        }));
    };

    // Get file icon based on file extension
    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        
        switch (extension) {
            case 'pdf':
                return <FaFilePdf className="text-red-500" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'svg':
            case 'webp':
                return <FaFileImage className="text-blue-400" />;
            case 'py':
            case 'js':
            case 'html':
            case 'css':
            case 'java':
            case 'c':
            case 'cpp':
            case 'h':
            case 'php':
            case 'rb':
            case 'go':
            case 'rs':
            case 'ts':
            case 'jsx':
            case 'tsx':
                return <FaFileCode className="text-green-500" />;
            case 'zip':
            case 'rar':
            case 'tar':
            case 'gz':
            case '7z':
                return <FaFileArchive className="text-yellow-600" />;
            case 'csv':
                return <FaFileCsv className="text-green-600" />;
            case 'xls':
            case 'xlsx':
                return <FaFileExcel className="text-green-700" />;
            case 'doc':
            case 'docx':
                return <FaFileWord className="text-blue-600" />;
            case 'ppt':
            case 'pptx':
                return <FaFilePowerpoint className="text-orange-600" />;
            default:
                return <FaFileAlt className="text-gray-500" />;
        }
    };

    // Handle file download using GET and token in query string
    const handleDownload = (filePath) => {
        try {
            const token = getCookie('csrftoken');
            const url = `/api/download_file/?token=${encodeURIComponent(token)}&path=${encodeURIComponent(filePath)}`;
            
            toast.info('Starting download...', {
                position: 'bottom-right',
                autoClose: 2000,
            });
            
            window.open(url, '_blank');
            
        } catch (error) {
            toast.error('Failed to download file. Please try again.', {
                position: 'bottom-right',
            });
            console.error('Download error:', error);
        }
    };

    // Select a file to show details
    const selectFile = (filePath, isFile) => {
        if (isFile) {
            setSelectedFile(filePath);
        }
    };

    // Get file name from path
    const getFileName = (path) => {
        return path ? path.split('/').pop() : '';
    };

    // Format file size (this would need to be implemented with actual file sizes from the server)
    const formatFileSize = (size) => {
        if (!size) return 'Unknown size';
        
        const units = ['B', 'KB', 'MB', 'GB'];
        let formattedSize = size;
        let unitIndex = 0;
        
        while (formattedSize >= 1024 && unitIndex < units.length - 1) {
            formattedSize /= 1024;
            unitIndex++;
        }
        
        return `${formattedSize.toFixed(1)} ${units[unitIndex]}`;
    };

    // Recursive function to render folders and files as a vertical tree structure
    const renderFileStructure = (structure, depth = 0, parentPath = '') => {
        return Object.keys(structure).map((key) => {
            const isFile = typeof structure[key] === 'string';
            const path = parentPath ? `${parentPath}/${key}` : key;
            const isOpen = openFolders[path];
            const isSelected = selectedFile === structure[key];

            return (
                <div key={path} className="flex flex-col">
                    <div 
                        className={`
                            flex items-center py-1 px-2 rounded transition-colors duration-150
                            ${isSelected ? 'bg-blue-100 dark:bg-blue-900' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
                        `}
                        onClick={() => selectFile(structure[key], isFile)}
                    >
                        <div style={{ width: depth * 16 }} className="flex-shrink-0" />
                        
                        {isFile ? (
                            <div className="flex items-center space-x-2">
                                {getFileIcon(key)}
                                <span className="text-gray-800 dark:text-gray-300">{key}</span>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-2 cursor-pointer" onClick={(e) => toggleFolder(path, e)}>
                                {isOpen ? (
                                    <FaChevronDown className="text-gray-500 dark:text-gray-400 text-xs" />
                                ) : (
                                    <FaChevronRight className="text-gray-500 dark:text-gray-400 text-xs" />
                                )}
                                {isOpen ? (
                                    <FaFolderOpen className="text-yellow-500 dark:text-yellow-400" />
                                ) : (
                                    <FaFolder className="text-yellow-500 dark:text-yellow-400" />
                                )}
                                <span className="text-gray-800 dark:text-gray-300 font-medium">{key}</span>
                            </div>
                        )}
                        
                        {isFile && (
                            <button
                                className="ml-auto text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900"
                                onClick={() => handleDownload(structure[key])}
                                title="Download file"
                            >
                                <FaDownload size={14} />
                            </button>
                        )}
                    </div>
                    
                    {!isFile && isOpen && (
                        <div className="relative">
                            <div className="absolute top-0 bottom-0 left-[7px] border-l border-gray-300 dark:border-gray-600 ml-[8px]" />
                            <div className="ml-4">
                                {renderFileStructure(structure[key], depth + 1, path)}
                            </div>
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div 
                ref={modalRef}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">File Explorer</h2>
                    <button 
                        onClick={onClose} 
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Close"
                    >
                        <FaTimes size={18} />
                    </button>
                </div>
                
                {/* Search */}
                <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search files..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full py-2 px-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <FaSearch className="absolute left-3 top-3 text-gray-400" />
                        {searchTerm && (
                            <button
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                onClick={() => setSearchTerm('')}
                            >
                                <FaTimes size={14} />
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Main content */}
                <div className="flex flex-grow overflow-hidden">
                    {/* File tree */}
                    <div className="w-2/3 p-4 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
                        {Object.keys(filteredStructure).length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 py-8">
                                <FaFileAlt size={32} className="mb-2" />
                                {searchTerm ? (
                                    <p>No files match your search.</p>
                                ) : (
                                    <p>No files available.</p>
                                )}
                            </div>
                        ) : (
                            renderFileStructure(filteredStructure)
                        )}
                    </div>
                    
                    {/* File details */}
                    <div className="w-1/3 p-4 overflow-y-auto">
                        {selectedFile ? (
                            <div className="h-full flex flex-col">
                                <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">File Details</h3>
                                <div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4 mb-4">
                                    <div className="flex justify-center mb-4">
                                        {getFileIcon(getFileName(selectedFile))}
                                    </div>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                        <span className="font-medium">Name:</span> {getFileName(selectedFile)}
                                    </p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                        <span className="font-medium">Path:</span> <span className="text-xs">{selectedFile}</span>
                                    </p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                        <span className="font-medium">Type:</span> {getFileName(selectedFile).split('.').pop().toUpperCase()}
                                    </p>
                                </div>
                                <div className="mt-auto">
                                    <button
                                        onClick={() => handleDownload(selectedFile)}
                                        className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center justify-center transition-colors"
                                    >
                                        <FaDownload className="mr-2" /> Download File
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                                <p>Select a file to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FileExplorerModal;